<template>
  <div class="pbx-svg-placeholder">
    <svg class="pbx-svg-placeholder__svg">
      <defs>
        <marker-head />
        <marker-head
          v-if="tmp_relationship"
          id="tmp-head"
          :ref_x="tmp_relationship.type === 'other' ? 14 : 0"
        />
      </defs>

      <g v-for="element in svg_relationships" :key="element.key" class="svg-relationship">
        <!--
          Линиии и кнопки на них
        -->
        <line-default v-bind="element.line" />

        <svg-button
          v-for="button in element.dots"
          :key="`${element.key}-${button.name}`"
          :ref="get_ref_name(element, button)"
          v-bind="button"
          @remove="remove_relationship(element, $event)"
        />
      </g>

      <line-default
        v-if="tmp_relationship && tmp_relationship.type !== 'self'"
        v-bind="tmp_relationship.line"
        marker_end="url(#tmp-head)"
      />
    </svg>

    <template v-for="element in svg_relationships">
      <!--
        Цветные точки по концам линии
      -->
      <connector-default
        :key="`connector-from-${element.key}`"
        :type="element.from.type"
        v-bind="element.line_start_marker"
      />

      <connector-default
        :key="`connector-to-${element.key}`"
        :type="element.from.type"
        v-bind="element.line_end_marker"
      />
    </template>

    <template v-if="tmp_relationship && tmp_relationship.type !== 'self'">
      <connector-default
        key="tmp-from"
        :type="tmp_relationship.from.type"
        v-bind="tmp_relationship.line_start_marker"
      />

      <connector-default
        v-if="tmp_relationship.type === 'other'"
        key="tmp-to"
        :type="tmp_relationship.from.type"
        v-bind="tmp_relationship.line_end_marker"
      />
    </template>
  </div>
</template>

<script>
  import { WorksheetActionModalService } from '@autoinformer/modals/WorksheetActionModalService';
  import { mapGetters, mapActions } from 'vuex';
  import {
    getCenterBox,
    getConnectors,
    getMarkers,
    transform2Coords
  } from '@/plugins/dnd-zone/utils';

  import defaultsDeep from 'lodash-es/defaultsDeep';
  import MarkerHead from './marker/head';
  import LineDefault from './line/default';
  import ConnectorDefault from './connector/default';
  import SvgButton from './button';

  export default {
    components: {
      MarkerHead,
      LineDefault,
      ConnectorDefault,
      SvgButton
    },
    data() {
      return {
        svg_relationships: []
      };
    },
    computed: {
      ...mapGetters('autoinformer/worksheets/form', {
        relationships: 'getRelationships',
        tmp_connection_from: 'getConnectionFrom',
        tmp_connection_to: 'getConnectionTo',
        cursor_position: 'getCurrentCursorPosition',
        worksheet_id: 'getId',
        get_action_by_id: 'getActionById'
      }),

      tmp_relationship: function() {
        /**
         * type: ['self', 'other', 'cursor']
         * self - поптыка соеденить блок сам с собой
         * other - попытка соеденить блок с другим блоком
         * cursor - стрелка двигается за курсором
         */
        if (this.tmp_connection_from) {
          let type = 'cursor';

          if (this.tmp_connection_to) {
            type = this.tmp_connection_from === this.tmp_connection_to ? 'self' : 'other';
          }

          const start = {
            type: this.tmp_connection_from.type,
            ...this.tmp_connection_from.position
          };

          const end = this.tmp_connection_to
            ? getCenterBox(this.tmp_connection_to.position)
            : this.cursor_position;

          const dots = getConnectors(getCenterBox(start), end);

          const default_calc = {
            from: start,
            line_start_marker: { x: dots.from.x - 5, y: dots.from.y - 5 },
            line_end_marker: { x: dots.to.x - 5, y: dots.to.y - 5 },
            line: {
              x1: dots.from.x,
              y1: dots.from.y,
              x2: dots.to.x,
              y2: dots.to.y
            },
            type: type
          };

          if (['self', 'other'].includes(type)) {
            return default_calc;
          } else {
            return defaultsDeep(
              {
                line_end_marker: { x: end.x - 5, y: end.y - 5 },
                line: {
                  x2: end.x,
                  y2: end.y
                }
              },
              default_calc
            );
          }
        }

        return null;
      }
    },

    mounted() {
      setInterval(() => this.re_draw(), 20);
    },

    methods: {
      ...mapActions('autoinformer/worksheets/form', {
        save_item: 'saveItem'
      }),

      re_draw: function() {
        this.svg_relationships = this.relationships
          .map(el => {
            const { from, to, keys } = el;
            const $from = document.getElementById(`box-${from.type}-${from.id}`);
            const $to = document.getElementById(`box-${to.type}-${to.id}`);

            if ($from && $to) {
              const from_transform = window.getComputedStyle($from).transform;
              const to_transform = window.getComputedStyle($to).transform;

              const center_from = getCenterBox(transform2Coords(from_transform));
              const center_to = getCenterBox(transform2Coords(to_transform));

              const coords = getConnectors(center_from, center_to);

              const svg_coords = {
                x1: coords.from.x,
                y1: coords.from.y,
                x2: coords.to.x,
                y2: coords.to.y
              };

              return {
                key: `from-${from.id}-to-${to.id}`,
                from: from,
                to: to,
                line: svg_coords,
                line_start_marker: { x: svg_coords.x1 - 5, y: svg_coords.y1 - 5 },
                line_end_marker: { x: svg_coords.x2 - 5, y: svg_coords.y2 - 5 },
                dots: getMarkers({ ...from, ...coords.from }, coords.to, keys)
              };
            }
          })
          .filter(x => x);
      },

      get_ref_name(element, button) {
        return `svg-button-${element.key}-${button.name}`;
      },

      remove_relationship: function(element, button) {
        const from_id = element.from.id;
        const ref_name = this.get_ref_name(element, button);
        const $el = this.$refs[ref_name][0].$el;

        WorksheetActionModalService.remove_relationship(
          this.worksheet_id,
          this.get_action_by_id(from_id),
          button.name,
          $el
        ).on('success', item =>
          this.save_item({
            id: item.id,
            item: item
          })
        );
      }
    }
  };
</script>

<style lang="scss">
  .pbx-svg-placeholder {
    height: 0;
    width: 0;
    overflow: visible;

    &__svg {
      min-width: 4000px;
      min-height: 4000px;
      position: absolute;
      z-index: 1;
    }
  }

  .svg-relationship {
    &:hover {
      cursor: pointer;

      .arrow-line {
        stroke: #0b90c4;
      }

      /**
      Стремная логика отображения иконок у типов.
      Для связи next - обычно нет иконки, но при наведении на линю, должна появляться иконка удаления
      Но есть исключения ['call', 'queue'] - для них есть иконка, удаление должно показываться только при наведении на нее.
       */
      .svg-button--next {
        .svg-button__item--remove {
          display: block;
        }

        &.svg-button--call,
        &.svg-button--queue {
          .svg-button__item--remove {
            display: none;
          }
        }
      }
    }
  }
</style>
